import { Link } from "react-router-dom";
import { useSeo } from "@/utils/seo";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

function Row({ label, children, testid }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3 border-b border-slate-100" data-testid={testid}>
            <span className="sm:w-64 shrink-0 font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
            <span className="text-sm sm:text-base text-slate-800 leading-relaxed">{children}</span>
        </div>
    );
}

export default function ImpressumPage() {
    const { t } = useLang();
    useSeo(
        "Impressum | Müller Satelliten- und Antennentechnik GmbH",
        t(
            "Impressum der Müller Satelliten- und Antennentechnik GmbH, Hemhofen — Angaben gemäß § 5 DDG.",
            "Impressum (legal notice) of Müller Satelliten- und Antennentechnik GmbH, Hemhofen — information pursuant to § 5 DDG (German Digital Services Act)."
        ),
    );
    return (
        <main data-testid="page-impressum" className="pt-28 sm:pt-32 pb-20 sm:pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <Eyebrow>{t("Rechtliches", "Legal")}</Eyebrow>
                    <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                        Impressum
                    </h1>
                    <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
                        {t(
                            "Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz).",
                            "Information pursuant to § 5 DDG (German Digital Services Act)."
                        )}
                    </p>
                </Reveal>

                <Reveal delay={0.05}>
                    <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-7 sm:p-9 max-w-3xl">
                        <Row label={t("Anbieter", "Provider")} testid="impressum-anbieter">
                            <strong className="font-semibold">Müller Satelliten- und Antennentechnik GmbH</strong>
                            <br />
                            Barthelweiher 14
                            <br />
                            91334 Hemhofen · {t("Deutschland", "Germany")}
                        </Row>
                        <Row label={t("Vertreten durch", "Represented by")} testid="impressum-geschaeftsfuehrer">
                            {t("Geschäftsführer", "Managing director")}: Erik Sap
                        </Row>
                        <Row label={t("Kontakt", "Contact")} testid="impressum-kontakt">
                            Tel. +49 (0) 9195 / 9404-0
                            <br />
                            Fax +49 (0) 9195 / 9404-40
                            <br />
                            <a
                                href="mailto:vertrieb@satmueller.de"
                                data-testid="impressum-mail-link"
                                className="font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5 transition-colors"
                            >
                                vertrieb@satmueller.de
                            </a>
                        </Row>
                        <Row label={t("Registereintrag", "Register entry")} testid="impressum-register">
                            {t("Registergericht", "Register court")}: Amtsgericht Fürth
                            <br />
                            HRB 8654
                        </Row>
                        <Row label={t("Umsatzsteuer-ID", "VAT ID")} testid="impressum-ust">
                            {t("Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG:", "VAT identification number pursuant to § 27 a UStG:")}
                            <br />
                            <span className="font-mono">DE218585027</span>
                        </Row>
                        <Row label={t("Verantwortlich für den Inhalt", "Responsible for content")} testid="impressum-mstv">
                            {t("Verantwortlich nach § 18 Abs. 2 MStV:", "Responsible pursuant to § 18 (2) MStV:")}
                            <br />
                            Erik Sap, {t("Anschrift wie oben", "address as above")}
                        </Row>
                        <Row label={t("Gerichtsstand", "Place of jurisdiction")} testid="impressum-gerichtsstand">
                            Erlangen
                        </Row>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-8 max-w-3xl text-sm text-slate-600 leading-relaxed space-y-4">
                        <div>
                            <h2 className="font-display text-lg font-bold text-slate-900">{t("EU-Streitschlichtung", "EU dispute resolution")}</h2>
                            <p className="mt-2">
                                {t(
                                    "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: ",
                                    "The European Commission provides a platform for online dispute resolution (ODR): "
                                )}
                                <a
                                    href="https://ec.europa.eu/consumers/odr/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="impressum-os-link"
                                    className="font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5 transition-colors"
                                >
                                    ec.europa.eu/consumers/odr
                                </a>
                                .{" "}
                                {t(
                                    "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
                                    "We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board."
                                )}
                            </p>
                        </div>
                        <p className="text-xs text-slate-400">
                            {t(
                                "Hinweis: Diese Angaben werden von der Müller Satelliten- und Antennentechnik GmbH bereitgestellt. Bei Fragen zum Impressum: vertrieb@satmueller.de",
                                "Note: This information is provided by Müller Satelliten- und Antennentechnik GmbH. Questions about the impressum: vertrieb@satmueller.de"
                            )}
                        </p>
                    </div>
                </Reveal>

                <div className="mt-10">
                    <Link
                        to="/"
                        data-testid="impressum-back-home"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-red-700 transition-colors"
                    >
                        <ArrowUpRight className="h-4 w-4 rotate-180" />
                        {t("Zur Startseite", "Back to the home page")}
                    </Link>
                </div>
            </div>
        </main>
    );
}
